import { isEmpty } from "lodash-es";
import { useSupabaseLoader } from "../../../../../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../../../../../server/supabase/hooks/types";
import type { UUID } from "../../../../../../../utils/uuid";
import {
  SupabaseSegmentService,
  type SupabaseSegment,
} from "../../../../../../segment/server/supabase-segment-service";
import { type PreviewDataRecipient } from "../../../../../server/supabase-preview-service";
import type { PreviewMomentData } from "../types";

export type DynamicSegmentRecipientData = Record<
  string,
  { cleanSegmentId: string; data: PreviewDataRecipient[]; name?: string }
>;

type Data = {
  channel: PreviewMomentData["channel"];
  dynamic_segment_id_map: PreviewMomentData["dynamic_segment_id_map"];
  target_connection_type: PreviewMomentData["target_connection_type"];
};

export function useDynamicSegmentRecipients({
  channel,
  dynamic_segment_id_map,
  target_connection_type,
}: Data): UseSupabaseLoaderState<DynamicSegmentRecipientData> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (!channel || isEmpty(dynamic_segment_id_map)) return {};

      async function getSegments(): Promise<SupabaseSegment[]> {
        if (isEmpty(dynamic_segment_id_map)) return [];

        const segmentService = new SupabaseSegmentService(supabase);

        const segmentQuery = segmentService.client
          .from(segmentService.table)
          .select("*")
          .in(
            "id",
            Object.keys(dynamic_segment_id_map).map((x) => x as UUID),
          );
        const { data, error } = await segmentQuery;
        if (error) throw error;

        return data;
      }

      const segments = await getSegments();

      const dynamic_segments = await Promise.all([
        ...Object.entries(dynamic_segment_id_map).map(async ([segmentId, cleanSegmentId]) => {
          const segment = segments.find((x) => x.id === (segmentId as UUID));
          if (!segment) return { data: [], segmentId, cleanSegmentId };

          const { data, error } = await supabase.rpc(
            "get_preview_data_segment_json_for_connection",
            {
              segment_json: segment.segment_json,
              account_id_: account_id,
              channel_: channel,
              connection_key_: target_connection_type ?? undefined,
            },
          );

          if (error) throw error;

          return { data, segmentId, cleanSegmentId };
        }),
      ]);

      return dynamic_segments.reduce((acc, { data, segmentId, cleanSegmentId }) => {
        acc[segmentId] = {
          name: segments.find((x) => x.id === (segmentId as UUID))?.name ?? undefined,
          cleanSegmentId,
          data,
        };
        return acc;
      }, {} as DynamicSegmentRecipientData);
    },
    [channel, dynamic_segment_id_map, target_connection_type],
  );
}
